import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AdjustmentModifyRequest, BalanceSheet, Company, FieldGroupValue,
  FieldValue, FieldValueReset, IncomeStatement, IncomeStatementRequest,
  Ownership, Report, ReportNote, ReportSummaryRequest, ReportVersion,
  RestResponse, Risk, Location, Projection, OwnerAssistEntity, QuickReport, ReportRelease
} from '../model';
import { ApiHeaders } from './headers';

@Injectable()
export class ReportProvider {

  constructor(private http: HttpClient, private datePipe: DatePipe) { }

  getReportListForDashboard(reportSummary: ReportSummaryRequest): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/report/list?` +
      `page=${reportSummary.PAGE}` +
      `&size=${reportSummary.SIZE}` +
      `&sort=${reportSummary.SORT}` +
      `&order=${reportSummary.ORDER}` +
      `&query=${reportSummary.escapedQuery}` +
      `&mode=${reportSummary.MODE}`, ApiHeaders.getOptions());
  }

  createReport(rpt: Report) {
    return this.http.post<RestResponse>(`/api/report`, rpt, ApiHeaders.getOptions());
  }

  deleteReport(reportId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/report/${reportId}`, ApiHeaders.getOptions());
  }

  getReportDetails(reportId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/report/${reportId}`, ApiHeaders.getOptions());
  }

  getReportDetailsByVersionId(reportId: string, versionId: string) {
    return this.http.get<RestResponse>(`/api/report/${reportId}/version/${versionId}`, ApiHeaders.getOptions());
  }

  saveReportVersion(reportId: string, version: ReportVersion): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${reportId}/version`, version, ApiHeaders.getOptions());
  }

  saveCompanyDetails(versionId: string, company: Company): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/company`, company, ApiHeaders.getOptions());
  }

  getNaics(page: number = 1, size: number = 20, query: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/naics?` +
      `page=${page}` +
      `&size=${size}` +
      `&query=${query}`, ApiHeaders.getOptions());
  }

  getFranchise(page: number = 1, size: number = 20, query: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/franchise?` +
      `page=${page}` +
      `&size=${size}` +
      `&query=${query}`, ApiHeaders.getOptions());
  }

  downloadReport(versionId: string, isOriginal: boolean = false): Observable<any> {
    return this.http.get(`/api/rv/${versionId}/pdf` + (isOriginal ? `?original=true` : ``), ApiHeaders.getOptionsPlainBlob());
  }

  getReportVersions(reportId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/report/${reportId}/versions`, ApiHeaders.getOptions());
  }

  createNewReportVersion(reportId: string, versionName: string = ''): Observable<RestResponse> {
    return this.http.put<RestResponse>(`/api/report/${reportId}/versions?name=${versionName}`, ApiHeaders.getOptions());
  }

  deleteReportVersion(reportId: string, versionId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/report/${reportId}/versions/${versionId}`, ApiHeaders.getOptions());
  }

  makeVersionMainReport(reportId: string, versionId: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${reportId}/versions/${versionId}`, null, ApiHeaders.getOptions());
  }

  //#region "Ownership"
  getOwnerList(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/ownership`, ApiHeaders.getOptions());
  }

  saveOwner(versionId: string, owner: Ownership): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/ownership`, owner, ApiHeaders.getOptions());
  }

  addDefaultOwner(versionId: string, noOfOwner: number): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/ownership/init/${noOfOwner}`, ApiHeaders.getOptions());
  }

  deleteOwner(versionId: string, ownerId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/ownership/${ownerId}`, ApiHeaders.getOptions());
  }

  getFairMarketReplacementSalary(versionId: string, mainOwnerId: string = '', wizardArray: Array<OwnerAssistEntity> = []): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/ownership/auto/fmr?o=${mainOwnerId}`, wizardArray, ApiHeaders.getOptions());
  }

  getGeneralManagerAutoCalcValue(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/ownership/auto/gm`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Location"
  getLocationList(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/location`, ApiHeaders.getOptions());
  }

  saveLocation(versionId: string, location: Location): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/location`, location, ApiHeaders.getOptions());
  }

  addDefaultLocation(versionId: string, noOfLocation: number): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/location/init/${noOfLocation}`, ApiHeaders.getOptions());
  }

  deleteLocation(versionId: string, locationId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/location/${locationId}`, ApiHeaders.getOptions());
  }

  checkSQFQuestion(versionId: string, locationId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/location/${locationId}/assist`, ApiHeaders.getOptions());
  }

  //#endregion

  //#region "Report Note"
  getReportNote(reportVersionId: string, reportPageId: number = null): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${reportVersionId}/notes`, ApiHeaders.getOptions());
  }

  saveReportNote(reportVersionId: string, noteObj: ReportNote): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${reportVersionId}/notes`, noteObj, ApiHeaders.getOptions());
  }

  deleteReportNote(reportVersionId: string, noteId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/rv/${reportVersionId}/notes/${noteId}`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Income Table"
  getIncomeStatementList(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/is`, ApiHeaders.getOptions());
  }

  saveIncomeStatementInit(versionId: string, objISR: IncomeStatementRequest): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/is/init`, objISR, ApiHeaders.getOptions());
  }

  saveIncomeStatement(versionId: string, incSta: IncomeStatement): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/is`, incSta, ApiHeaders.getOptions());
  }

  deleteIncomeStatement(versionId: string, incStaId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/is/${incStaId}`, ApiHeaders.getOptions());
  }

  resetIncomeStatement(versionId: string) {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/is/reset`, ApiHeaders.getOptions());
  }

  removeInterim(versionId: string) {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/is/interim`, ApiHeaders.getOptions());
  }

  addInterim(versionId: string, objISR: IncomeStatementRequest) {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/is/interim`, objISR, ApiHeaders.getOptions());
  }

  actionApply(versionId: string, fieldName: string, functionName: string, value: any) {
    if (isNaN(value))
      value = 0;
    else
      value = Number(value);

    const param = {
      field: fieldName,
      function: functionName,
      value: value
    }
    return this.http.post<RestResponse>(`/api/rv/${versionId}/is/action`, param, ApiHeaders.getOptions());
  }

  excelImportIS(versionId: string, excelFile: FormData): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/is/import`, excelFile);
  }

  downloadIsTemplate(versionId: string): Observable<any> {
    return this.http.get(`/api/rv/${versionId}/is/template`, ApiHeaders.getOptionsPlainBlob());
  }

  downloadChecklistPDF(): Observable<any> {
    return this.http.get(environment.ASSETS_URL + 'resources/checklist.pdf', ApiHeaders.getOptionsPlainBlob());
  }

  //#endregion

  //#region "Report Common Update"
  updateReportField(ObjectId: string, values: FieldValue): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${ObjectId}/field`, values, ApiHeaders.getOptions());
  }
  updateReportFieldGroup(ObjectId: string, values: FieldGroupValue): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${ObjectId}/field/group`, values, ApiHeaders.getOptions());
  }
  resetReportField(ObjectId: string, values: FieldValueReset): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${ObjectId}/field/reset`, values, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Balance-Sheet"
  saveBalanceSheet(versionId: string, values: BalanceSheet): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/balancesheet`, values, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Adjustment"
  getAdjustment(versionId: string) {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/adjustment`, ApiHeaders.getOptions());
  }

  saveAdjustment(versionId: string, values: AdjustmentModifyRequest): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/adjustment`, values, ApiHeaders.getOptions());
  }

  deleteAdjustment(versionId: string, adjustmentId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/adjustment/${adjustmentId}`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Risk"
  createRisk(versionId: string, values: Risk): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/risk`, values, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Final Calculation"
  validateFullReport(reportId: string) {
    return this.http.get<RestResponse>(`/api/rv/${reportId}/validate`, ApiHeaders.getOptions());
  }

  calculateFinalValue(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/final`, ApiHeaders.getOptions());
  }

  downloadSampleReport(): Observable<any> {
    return this.http.get(environment.ASSETS_URL + 'resources/report.pdf', ApiHeaders.getOptionsPlainBlob());
  }

  //#endregion

  //#region "Call WS Update"
  updateWS(versionId: string, wsGroupName: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/calc/${wsGroupName}`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Projection"
  getProjection(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/projection`, ApiHeaders.getOptions());
  }

  deleteProjection(versionId: string, projectionId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/rv/${versionId}/projection/${projectionId}`, ApiHeaders.getOptions());
  }

  addProjection(versionId: string, obj: Projection): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/projection`, obj, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Weighting"
  getWeighting(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/weighting`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Payment"
  // no use
  paymentIntent(amount: number = 1, promo: string = ''): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/billing/purchase/credit?` +
      `amount=${amount}` +
      `&promo=${promo}`, ApiHeaders.getOptions());
  }

  purchaseReport(reportId: string = '', promo: string = ''): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/billing/purchase/report/${reportId}?` +
      `&promo=${promo}`, ApiHeaders.getOptions());
  }

  applyPromo(reportId: string = '', promo: string = ''): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/billing/purchase/promo/${reportId}?` +
      `&promo=${promo}`, ApiHeaders.getOptions());
  }

  // no use
  applyPromoOld(amount: number = 1, promo: string = ''): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/billing/purchase/promo?` +
      `amount=${amount}` +
      `&promo=${promo}`, ApiHeaders.getOptions());
  }

  spendCredit(reportId: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/billing/unlock/${reportId}`, null, ApiHeaders.getOptions());
  }

  getBillingTransactions(pageNumber: number = 1): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/billing?page=${pageNumber}`, ApiHeaders.getOptions());
  }

  getBillingStatus(reportId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/billing/status/${reportId}`, ApiHeaders.getOptions());
  }

  lockReport(reportId: string) {
    return this.http.post<RestResponse>(`/api/billing/lock/${reportId}`, null, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Quick Report"
  callQuickReport(quickRpt: QuickReport): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/quick-report`, quickRpt, ApiHeaders.getOptions());
  }

  callStatisticsExpress(): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/statistics/express`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Feedback"
  addFeedback(fbObj: any): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/feedback`, fbObj, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Share reports"
  getOrgUserListOfOrg(page: number = 1, size: number = 20, query: string = ''): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/org/users?` +
        `page=${page}` +
        `&size=${size}` +
        `&query=${query}`, ApiHeaders.getOptions());
  }

  shareReport(reportId: string, email: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/share/${reportId}/email`, email, ApiHeaders.getOptions());
  }

  removeShareReport(reportId: string, userId: string): Observable<RestResponse> {
    return this.http.delete<RestResponse>(`/api/share/${reportId}/${userId}`, ApiHeaders.getOptions());
  }

  getUsersHasBeenShared(reportId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/share/${reportId}/users`, ApiHeaders.getOptions());
  }

  sharedWithAllOrgUsers(reportId: string, org_share: boolean): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/share/${reportId}?org_share=${org_share}`, null, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "release reports"
  releaseReport(reportVersionId: string, obj: ReportRelease): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/release/${reportVersionId}/email`, obj, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "Resources"
  getResources(): Observable<RestResponse> {
    return this.http.get<any>(`https://resources.valzy.com/api/tags`, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "transfer reports"
  transferReport(reportId: string, email: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${reportId}/transfer`, email, ApiHeaders.getOptions());
  }
  //#endregion

  //#region "ALG Version"
  updateVersionOfReport(reportId: string, reportVersionId: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/report/${reportId}/versions/${reportVersionId}/alg-upgrade`, null, ApiHeaders.getOptions());
  }
  //#endregion

  getReportPreview(versionId: string): Observable<RestResponse> {
    return this.http.get<RestResponse>(`/api/rv/${versionId}/pdf/preview`, ApiHeaders.getOptions());
  }

  sendReportPreview(versionId: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/rv/${versionId}/pdf/send`, null, ApiHeaders.getOptions());
  }
}
